import React, { useCallback, useEffect } from 'react';
import { RecordTime, Showtime, StopButton, Time, RecordText } from './styles';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { useLocation } from 'react-router-dom';
import {
  convertBase64ToFile,
  convertBlobUrlToDataUrl,
  formatSecondsDuration,
  getFirebaseUrlFromFile,
  isEmpty
} from '../../../helpers/common';
import {
  ICON_BUTTON_SIZE,
  ICON_BUTTON_TYPE,
  ICON_SIZE,
  MAX_RECORDING_FILE_SIZE,
  MAX_RECORDING_FILE_SIZE_LABEL,
  SHARE_TYPE
} from '../../../global/constants';
import { useMediaRecorder } from '../../../hooks/useMediaRecorder';
import copy from 'clipboard-copy';
import { captureException } from '../../../services/logService';
import { addShareData, dispatchProjectSuccess } from '../../../services/projectServices';
import { getSnapListByUser } from '../../../services/snapService';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import IconButton from '../../button/iconButton';

const RecordingNavbar: React.FC = () => {
  // Mobx store variables
  const { projectStore, appStore } = rootStore;
  const { uploadFilePercent } = projectStore;
  const { screenRecordingLoading, screenRecordingUrl } = appStore;
  //Other variable
  const location = useLocation();
  const { status, stopRecording, mediaBlobUrl, clearBlobUrl, duration } = useMediaRecorder();

  const onScreenCaptureComplete = useCallback(
    async (mediaBlobUrl: string) => {
      try {
        rootStore.appStore.setScreenRecordingLoading(true);
        if (!isEmpty(mediaBlobUrl)) {
          const baseFile = await convertBlobUrlToDataUrl(mediaBlobUrl || '')
            .then((regularUrl) => regularUrl)
            .catch((error) => {
              captureException(error);
              return undefined;
            });
          if (!baseFile) return;
          const fileData: File | undefined = await convertBase64ToFile(baseFile, true);
          if (fileData && fileData?.size > MAX_RECORDING_FILE_SIZE) {
            dispatchProjectSuccess(`Please upload a file smaller than ${MAX_RECORDING_FILE_SIZE_LABEL}.`);
            return false;
          }
          const firebaseUrl = await getFirebaseUrlFromFile({ file: fileData }, 'screen_recordings/');
          const sharePayload = {
            ref: firebaseUrl,
            type: SHARE_TYPE.snap
          };
          const shareResponse = await addShareData(sharePayload);
          if (location?.pathname === '/snap') {
            getSnapListByUser();
          }
          rootStore.appStore.setScreenRecordingUrl(
            shareResponse?.id ? `https://share.teamcamp.app/${shareResponse.id}` : ''
          );
        }
      } catch (e) {
        captureException(e);
      } finally {
        clearBlobUrl();
        rootStore.appStore.setScreenRecordingLoading(false);
      }
    },
    [clearBlobUrl, location?.pathname]
  );

  useEffect(() => {
    if (!isEmpty(mediaBlobUrl) && !location.pathname?.includes('/tasks/details/'))
      onScreenCaptureComplete(mediaBlobUrl || '');
  }, [location.pathname, mediaBlobUrl, onScreenCaptureComplete]);

  const onCopyLink = useCallback(async (url: string) => {
    try {
      await copy(url);
      dispatchProjectSuccess('Screen recording url copied successfully!');
    } catch (error) {
      captureException(error);
    }
  }, []);

  const onCloseUrlBox = useCallback(() => {
    rootStore.appStore.setScreenRecordingUrl('');
  }, []);

  return (
    <>
      {status === 'recording' && (
        <RecordTime>
          <Showtime>
            <RecordText hasHours={formatSecondsDuration(duration, 'label')?.includes('h')}>
              <SVGIcon
                name='record-start-icon'
                width={ICON_SIZE.LG}
                height={ICON_SIZE.LG}
                viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                className='record-icon'
              />
              <p>Screen Recording</p>
            </RecordText>
            <Time>{formatSecondsDuration(duration, 'label')}</Time>
          </Showtime>
          <StopButton role='button' tabIndex={0} onClick={stopRecording}>
            Stop
          </StopButton>
        </RecordTime>
      )}
      {(!isEmpty(screenRecordingUrl) || screenRecordingLoading) && (
        <RecordTime className='copyClip'>
          <Showtime>
            <RecordText isRecorded={true}>
              <SVGIcon
                name='snap-icon'
                width={ICON_SIZE.LG}
                height={ICON_SIZE.LG}
                viewBox={`0 0 ${ICON_SIZE.LG} ${ICON_SIZE.LG}`}
                fill='var(--text-primary)'
              />
              <p>Snap Recorded</p>
            </RecordText>
            <IconButton
              iconName='new-close-icon'
              size={ICON_BUTTON_SIZE.EXTRA_SMALL}
              variant={ICON_BUTTON_TYPE.TERTIARY}
              onClick={onCloseUrlBox}
            />
          </Showtime>
          {!screenRecordingLoading ? (
            <StopButton role='button' tabIndex={0} onClick={() => onCopyLink(screenRecordingUrl)}>
              Copy link
            </StopButton>
          ) : (
            <StopButton>Uploading({Math.floor(uploadFilePercent)}%)</StopButton>
          )}
        </RecordTime>
      )}
    </>
  );
};

export default observer(RecordingNavbar);
